import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowRight,
  Download,
  Mail,
  Instagram,
  Music2,
  Facebook,
  ExternalLink,
  MapPin,
  Check,
  Compass,
  Waves,
  Globe2,
  Camera,
  Sparkles,
  TrendingUp,
  Users,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Award,
  Anchor,
  Menu,
  X,
} from "lucide-react";
import MediaKitDialog from "../components/MediaKitDialog";
import {
  LOGOS,
  IMAGES,
  NAV_LINKS,
  HERO_PROOF,
  CREDENTIALS,
  CONTENT_PILLARS,
  REACH_STATS,
  PERFORMANCE_STATS,
  CONTENT_THEMES,
  PLATFORMS,
  AUDIENCE_GROUPS,
  AUDIENCE_MARKETS,
  AUDIENCE_INTERESTS,
  PARTNERSHIPS,
  PROJECTS,
  PROJECT_LOGOS,
  PROJECT_LINKS,
  WHY_VALUES,
  RATE_CARD,
  NEWSLETTER_FOCUS,
  CONTACT,
} from "../data";

// ---------- Small reusable bits ----------

const Eyebrow = ({ children }) => (
  <span className="eyebrow text-[#00B4D8]">{children}</span>
);

const SectionHeading = ({ eyebrow, title, intro, light = false, center = false }) => (
  <div className={"max-w-3xl " + (center ? "mx-auto text-center" : "")}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2
      className={
        "font-heading mt-3 text-3xl md:text-5xl font-light tracking-tight " +
        (light ? "text-[#050708]" : "text-white")
      }
    >
      {title}
    </h2>
    {intro && (
      <p
        className={
          "mt-5 text-base md:text-lg font-body leading-relaxed " +
          (light ? "text-[#061826]/75" : "text-white/70")
        }
      >
        {intro}
      </p>
    )}
  </div>
);

const Pill = ({ children, light = false }) => (
  <span
    className={
      "inline-flex items-center rounded-full border px-3.5 py-1.5 text-xs md:text-sm font-medium " +
      (light
        ? "border-[#061826]/15 bg-white text-[#061826]"
        : "border-white/10 bg-white/5 text-[#CAF0F8]")
    }
  >
    {children}
  </span>
);

// ---------- Navigation ----------

const Nav = ({ onCollab, onDownload }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={
        "fixed top-0 inset-x-0 z-50 transition-all duration-300 " +
        (scrolled
          ? "bg-[#061826]/90 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent")
      }
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src={LOGOS.emblem}
            alt="Traveler Submerged emblem"
            className="h-9 md:h-10 w-auto"
          />
          <span className="hidden sm:block font-heading text-sm md:text-base text-white/85 tracking-wide group-hover:text-white">
            Traveler Submerged
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={"#" + l.id}
              data-testid={"nav-link-" + l.id}
              className="text-sm text-white/65 hover:text-[#00B4D8] transition-colors font-body"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Button
            variant="ghost"
            data-testid="nav-download-btn"
            onClick={onDownload}
            className="text-white/80 hover:text-white hover:bg-white/5"
          >
            <Download className="h-4 w-4 mr-2" />
            Media kit
          </Button>
          <Button
            onClick={onCollab}
            data-testid="nav-collab-btn"
            className="bg-[#00B4D8] text-[#050708] hover:bg-[#0077B6] hover:text-white font-medium"
          >
            Request collaboration
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        <button
          className="md:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#050708]/95 backdrop-blur-xl border-t border-white/5">
          <div className="px-5 py-5 flex flex-col gap-3">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={"#" + l.id}
                onClick={() => setOpen(false)}
                className="text-white/80 py-2 border-b border-white/5 font-body"
              >
                {l.label}
              </a>
            ))}
            <div className="flex flex-col gap-2 pt-3">
              <Button
                onClick={() => { setOpen(false); onDownload(); }}
                variant="outline"
                className="border-white/10 text-white hover:bg-white/5"
              >
                <Download className="h-4 w-4 mr-2" /> Download media kit
              </Button>
              <Button
                onClick={() => { setOpen(false); onCollab(); }}
                className="bg-[#00B4D8] text-[#050708] hover:bg-[#0077B6] hover:text-white"
              >
                Request collaboration
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

// ---------- Hero ----------

const Hero = ({ onCollab, onDownload }) => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden"
  >
    {/* Background image */}
    <div className="absolute inset-0">
      <img
        src={IMAGES.hero}
        alt="Robin Bull diving in Procean drysuit"
        className="w-full h-full object-cover"
        style={{ objectPosition: "65% center" }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050708] via-[#050708]/85 to-[#050708]/30" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050708]" />
    </div>

    {/* Content */}
    <div className="relative z-10 max-w-7xl mx-auto w-full px-5 md:px-8 pt-32 md:pt-40 pb-10">
      <div className="max-w-4xl fade-up">
        <div className="flex items-center gap-3 mb-7">
          <span className="h-px w-10 bg-[#00B4D8]" />
          <Eyebrow>Dive media partner</Eyebrow>
        </div>

        <h1 className="sr-only">
          Traveler Submerged. Dive Media, Content Strategy and Industry Partnerships.
        </h1>

        <img
          src={LOGOS.wordmark}
          alt="Traveler Submerged"
          className="h-64 md:h-80 lg:h-[26rem] w-auto -ml-3 md:-ml-6 mb-2 -mt-4"
          data-testid="hero-wordmark"
        />

        <p className="font-heading text-2xl md:text-4xl lg:text-5xl font-light text-white leading-[1.1] tracking-tight max-w-3xl">
          Dive Media, Content Strategy
          <span className="block text-[#CAF0F8]/75">and Industry Partnerships.</span>
        </p>

        <p className="mt-7 max-w-2xl text-base md:text-lg text-white/75 font-body leading-relaxed">
          Founded by Robin Bull, Traveler Submerged connects dive brands, events and
          ocean-focused businesses with a highly engaged audience of scuba divers,
          freedivers, instructors, underwater creators and gear buyers through
          expert-led content, storytelling and digital strategy.
        </p>

        <div className="mt-9 flex flex-wrap gap-3">
          <Button
            data-testid="hero-collab-btn"
            onClick={onCollab}
            size="lg"
            className="bg-[#00B4D8] text-[#050708] hover:bg-[#0077B6] hover:text-white font-medium rounded-full px-6 h-12"
          >
            Request collaboration
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            data-testid="hero-download-btn"
            onClick={onDownload}
            size="lg"
            variant="outline"
            className="rounded-full px-6 h-12 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
          >
            <Download className="mr-2 h-4 w-4" />
            Download media kit
          </Button>
          <a
            href="#partnerships"
            data-testid="hero-options-btn"
            className="inline-flex items-center gap-2 px-6 h-12 rounded-full text-white/80 hover:text-white text-sm font-medium"
          >
            View partnership options
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </div>

    {/* Proof strip */}
    <div
      data-testid="hero-proof-strip"
      className="relative z-10 border-t border-white/10 bg-[#050708]/70 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-2 md:px-6">
        <div className="grid grid-cols-2 md:grid-cols-6 divide-x divide-white/8">
          {HERO_PROOF.map((s, i) => (
            <div key={i} className="px-4 md:px-6 py-5 md:py-6 text-left">
              <div className="stat-num text-2xl md:text-3xl text-white">{s.value}</div>
              <div className="mt-1 text-[10px] md:text-xs uppercase tracking-[0.18em] text-[#CAF0F8]/55">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ---------- Founder ----------

const Founder = () => (
  <section id="founder" data-testid="founder-section" className="bg-[#CAF0F8] py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-[#061826]">
            <img src={IMAGES.founder} alt="Robin Bull underwater" className="w-full h-full object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-[#061826]/10" />
            <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-[#050708]/90 to-transparent">
              <div className="text-white/85 text-sm font-heading">Robin Bull</div>
              <div className="text-[#CAF0F8]/80 text-xs">Founder, Traveler Submerged</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Eyebrow>Founder authority</Eyebrow>
          <h2 className="font-heading mt-3 text-3xl md:text-5xl font-light text-[#050708] tracking-tight">
            Meet Robin Bull.
          </h2>
          <p className="mt-5 text-[#061826]/75 text-base md:text-lg leading-relaxed font-body">
            Robin Bull is a PADI Staff Instructor, Freediving Instructor, Tec diver,
            underwater content creator and digital marketing strategist with more than
            3,000 dives across Malta, Mauritius, São Tomé, Greece and the wider
            Mediterranean. Through Traveler Submerged, Robin blends professional dive
            education, real-world gear experience, storytelling and digital strategy to
            help divers, dive centres and brands communicate with clarity online.
          </p>
          <p className="mt-4 text-[#061826]/70 text-base leading-relaxed font-body">
            His content speaks to divers who care about skill progression, better
            underwater awareness, safer diving, smarter gear choices and stronger
            digital presence. With a background in luxury hospitality, dive instruction
            and marketing, Robin brings rare crossover value to brands looking for
            credible visibility in the diving industry.
          </p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
            {CREDENTIALS.map((c) => (
              <div
                key={c}
                data-testid={"credential-" + c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
                className="lift rounded-sm bg-white border border-[#061826]/10 p-4 text-[#061826] text-sm font-medium"
              >
                <Award className="h-4 w-4 text-[#0077B6] mb-2" strokeWidth={1.5} />
                {c}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---------- About brand ----------

const AboutBrand = () => (
  <section id="about" data-testid="about-section" className="bg-[#050708] py-24 md:py-32 relative overflow-hidden">
    <div className="absolute -top-40 -right-40 w-[40rem] h-[40rem] rounded-full bg-[#0077B6]/10 blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
      <SectionHeading
        eyebrow="What we do"
        title="A specialist dive media brand, not a general travel feed."
        intro="Traveler Submerged creates practical, experience-led content for divers who want more than pretty underwater clips. The audience comes for gear insight, dive technique, training progression, honest reflections, underwater storytelling, content creation tips and real-world advice from someone who has worked inside the dive industry for years."
      />
      <p className="mt-5 max-w-3xl text-white/65 font-body leading-relaxed">
        For partners, Traveler Submerged offers a credible voice with strong field
        experience, content production ability and a clear understanding of how dive
        businesses sell, educate and build trust online.
      </p>

      <div className="mt-14">
        <Eyebrow>Core content pillars</Eyebrow>
        <div className="mt-5 flex flex-wrap gap-2.5">
          {CONTENT_PILLARS.map((p) => (
            <Pill key={p}>{p}</Pill>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ---------- Media reach dashboard ----------

const MediaReach = () => (
  <section id="reach" data-testid="reach-section" className="bg-[#061826] py-24 md:py-32 border-y border-white/5">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
        <SectionHeading
          eyebrow="Current media reach"
          title="A focused dive audience, measured and current."
        />
        <div className="md:max-w-sm text-white/60 text-sm font-body">
          Combined social audience of 31.2k+ across Instagram, Facebook and TikTok,
          with consistent monthly performance and strong international reach.
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {REACH_STATS.map((s, i) => (
          <div
            key={s.label}
            data-testid={"reach-stat-" + i}
            className="lift glass-dark rounded-sm p-6 md:p-7"
          >
            <div className="stat-num text-4xl md:text-5xl text-[#00B4D8]">{s.value}</div>
            <div className="mt-4 text-white text-sm md:text-base font-medium font-heading">{s.label}</div>
            <div className="mt-1 text-white/45 text-xs font-body">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 p-6 md:p-7 border border-[#00B4D8]/20 bg-[#00B4D8]/5 rounded-sm">
        <Eyebrow>Newsletter note</Eyebrow>
        <p className="mt-3 text-white/80 text-sm md:text-base font-body leading-relaxed max-w-4xl">
          Traveler Submerged is developing a more focused newsletter concept for divers,
          creators and dive professionals. The list now includes 393 subscribers, with
          future growth planned around content strategy, gear education, dive business
          insight and industry storytelling.
        </p>
      </div>
    </div>
  </section>
);

// ---------- Proven content performance ----------

const Performance = () => (
  <section data-testid="performance-section" className="bg-[#050708] py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <SectionHeading
        eyebrow="Proven content performance"
        title="9.3M video views. Built on practical dive education, not generic travel content."
        intro="From September 2025 to June 2026, Traveler Submerged's strongest content was practical, opinion-led and education-first. It earns attention because it helps divers think, improve and engage."
      />

      <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3">
        {PERFORMANCE_STATS.map((s, i) => (
          <div
            key={s.label}
            data-testid={"perf-stat-" + i}
            className="lift rounded-sm border border-white/8 p-5 md:p-6 bg-gradient-to-b from-white/4 to-transparent"
          >
            <div className="stat-num text-3xl md:text-4xl text-white">{s.value}</div>
            <div className="mt-2 text-[#CAF0F8]/70 text-xs md:text-sm uppercase tracking-[0.16em]">
              {s.label}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 grid lg:grid-cols-2 gap-8 lg:gap-10">
        <div className="glass-darker rounded-sm p-7 md:p-9">
          <div className="flex items-start gap-4">
            <TrendingUp className="h-6 w-6 text-[#00B4D8] mt-1" strokeWidth={1.4} />
            <div>
              <h3 className="font-heading text-xl text-white">Short-form video, where it counts.</h3>
              <p className="mt-3 text-white/65 font-body text-sm md:text-base leading-relaxed">
                Short-form video is the strongest format for Traveler Submerged,
                generating 9.3M views from 124 video posts, with an average of 75k views
                per video and a top-performing reel reaching 1.98M views. October 2025
                was the strongest month, with 3.69M video views and 2.15M reach.
              </p>
              <div className="mt-5 flex flex-wrap gap-3 text-xs text-white/55">
                <span className="px-3 py-1.5 rounded-full bg-white/5">352 posts analysed</span>
                <span className="px-3 py-1.5 rounded-full bg-white/5">124 video posts</span>
                <span className="px-3 py-1.5 rounded-full bg-white/5">9.4k shares</span>
                <span className="px-3 py-1.5 rounded-full bg-white/5">4.5k comments</span>
              </div>
            </div>
          </div>
        </div>

        <div className="glass-darker rounded-sm p-7 md:p-9">
          <div className="flex items-start gap-4">
            <Sparkles className="h-6 w-6 text-[#8FD6C8] mt-1" strokeWidth={1.4} />
            <div className="w-full">
              <h3 className="font-heading text-xl text-white">Best-performing content themes.</h3>
              <p className="mt-3 text-white/65 font-body text-sm md:text-base">
                Top reels cover mask clearing, air consumption, diver confidence, beginner
                mistakes and real-world underwater behaviour.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {CONTENT_THEMES.map((t) => (
                  <Pill key={t}>{t}</Pill>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---------- Platforms ----------

const PlatformIcon = ({ name }) => {
  if (name === "Instagram") return <Instagram className="h-5 w-5" strokeWidth={1.5} />;
  if (name === "Facebook") return <Facebook className="h-5 w-5" strokeWidth={1.5} />;
  return <Music2 className="h-5 w-5" strokeWidth={1.5} />;
};

const Platforms = () => (
  <section id="platforms" data-testid="platforms-section" className="bg-[#061826] py-24 md:py-32 border-y border-white/5">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <SectionHeading
        eyebrow="Platform strengths"
        title="Three channels. Each plays a distinct role."
      />

      <div className="mt-14 grid md:grid-cols-3 gap-4 md:gap-5">
        {PLATFORMS.map((p, idx) => (
          <div
            key={p.name}
            data-testid={"platform-" + p.name.toLowerCase()}
            className="lift glass-dark rounded-sm p-6 md:p-7 flex flex-col"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-[#00B4D8]">
                <PlatformIcon name={p.name} />
                <span className="font-heading text-lg text-white">{p.name}</span>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-white/45">
                {idx === 0 ? "Authority" : idx === 1 ? "Discovery" : "Growth"}
              </span>
            </div>
            <div className="mt-1 text-xs text-white/45 font-body">{p.handle}</div>

            <div className="mt-6 stat-num text-5xl text-white">{p.followers}</div>
            <div className="text-[#CAF0F8]/60 text-xs uppercase tracking-widest mt-1">
              Followers
            </div>

            <div className="mt-6 grid grid-cols-3 gap-2 border-y border-white/8 py-4">
              {p.metrics.map((m) => (
                <div key={m.l}>
                  <div className="stat-num text-base md:text-lg text-[#00B4D8]">{m.v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/45 mt-0.5">
                    {m.l}
                  </div>
                </div>
              ))}
            </div>

            <ul className="mt-5 space-y-2 text-sm text-white/70 font-body">
              {p.notes.map((n) => (
                <li key={n} className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 text-[#8FD6C8] mt-1 flex-shrink-0" />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Audience ----------

const Audience = () => (
  <section id="audience" data-testid="audience-section" className="bg-[#CAF0F8] py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <SectionHeading
        eyebrow="Audience profile"
        title="A commercially relevant dive audience."
        intro="Traveler Submerged reaches certified divers, instructors, gear buyers, underwater creators, freedivers and dive travel consumers across the United States, the United Kingdom, Europe and the wider Mediterranean."
        light
      />

      <div className="mt-14 grid lg:grid-cols-3 gap-4 md:gap-5">
        <div className="lift bg-white border border-[#061826]/10 rounded-sm p-7">
          <div className="flex items-center gap-3 text-[#0077B6]">
            <Users className="h-5 w-5" strokeWidth={1.4} />
            <span className="eyebrow text-[#0077B6]">Audience groups</span>
          </div>
          <ul className="mt-5 space-y-2 text-[#061826]/85 text-sm font-body">
            {AUDIENCE_GROUPS.map((g) => (
              <li key={g} className="flex items-start gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#00B4D8] mt-2 flex-shrink-0" />
                {g}
              </li>
            ))}
          </ul>
        </div>

        <div className="lift bg-white border border-[#061826]/10 rounded-sm p-7">
          <div className="flex items-center gap-3 text-[#0077B6]">
            <Globe2 className="h-5 w-5" strokeWidth={1.4} />
            <span className="eyebrow text-[#0077B6]">Primary markets</span>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {AUDIENCE_MARKETS.map((m) => (
              <Pill key={m} light>{m}</Pill>
            ))}
          </div>
          <div className="mt-7 border-t border-[#061826]/10 pt-5">
            <div className="eyebrow text-[#0077B6]">Core age</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="bg-[#061826] text-white rounded-sm p-4">
                <div className="text-xs text-white/55">Instagram</div>
                <div className="stat-num text-2xl mt-1">25-44</div>
              </div>
              <div className="bg-[#061826] text-white rounded-sm p-4">
                <div className="text-xs text-white/55">Facebook</div>
                <div className="stat-num text-2xl mt-1">35-64</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lift bg-white border border-[#061826]/10 rounded-sm p-7">
          <div className="flex items-center gap-3 text-[#0077B6]">
            <Compass className="h-5 w-5" strokeWidth={1.4} />
            <span className="eyebrow text-[#0077B6]">Interests</span>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {AUDIENCE_INTERESTS.map((i) => (
              <Pill key={i} light>{i}</Pill>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---------- Partnerships ----------

const Partnerships = ({ onCollab }) => (
  <section id="partnerships" data-testid="partnerships-section" className="bg-[#050708] py-24 md:py-32 relative overflow-hidden">
    <div className="absolute -bottom-40 -left-40 w-[40rem] h-[40rem] rounded-full bg-[#00B4D8]/8 blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
      <SectionHeading
        eyebrow="Partnership opportunities"
        title="Six ways to work together."
        intro="Each engagement is shaped around your campaign objectives, audience and timeline. Below is a starting point. Full scopes are confirmed during the brief."
      />

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {PARTNERSHIPS.map((p, i) => (
          <div
            key={p.title}
            data-testid={"partnership-" + i}
            className="lift glass-dark rounded-sm p-7 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <span className="stat-num text-2xl text-[#00B4D8]">0{i + 1}</span>
              <div className="h-px flex-1 bg-white/8" />
            </div>
            <h3 className="font-heading text-xl text-white mt-5">{p.title}</h3>
            <p className="mt-3 text-white/65 text-sm font-body leading-relaxed">{p.intro}</p>
            <div className="mt-5 pt-5 border-t border-white/8">
              <div className="text-[10px] uppercase tracking-widest text-[#CAF0F8]/55 mb-3">
                Deliverables
              </div>
              <ul className="space-y-1.5">
                {p.deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-2 text-sm text-white/75 font-body">
                    <Check className="h-3.5 w-3.5 text-[#00B4D8] mt-1 flex-shrink-0" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <Button
          data-testid="partnerships-cta"
          onClick={onCollab}
          size="lg"
          className="bg-[#00B4D8] text-[#050708] hover:bg-[#0077B6] hover:text-white rounded-full px-7 h-12"
        >
          Discuss a partnership
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  </section>
);

// ---------- Brands, clients, projects ----------

const Projects = () => (
  <section id="projects" data-testid="projects-section" className="bg-[#061826] py-24 md:py-32 border-y border-white/5">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <SectionHeading
        eyebrow="Brands, clients and projects"
        title="Dive, gear, conservation, wellness and hospitality."
        intro="Traveler Submerged has worked across dive, gear, conservation, wellness, hospitality and content-led tourism projects, combining strong visual storytelling with practical digital marketing experience."
      />

      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {PROJECT_LOGOS.map((b) => (
          <div
            key={b}
            data-testid={"brand-" + b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
            className="lift h-20 flex items-center justify-center bg-white/4 border border-white/8 rounded-sm px-3 text-center text-xs md:text-sm text-white/85 font-medium font-body"
          >
            {b}
          </div>
        ))}
      </div>

      <div className="mt-16 grid md:grid-cols-2 gap-4">
        {PROJECTS.map((p) => (
          <div
            key={p.name}
            className="border-l-2 border-[#00B4D8]/40 pl-5 py-1"
            data-testid={"project-note-" + p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
          >
            <div className="font-heading text-white text-base md:text-lg">{p.name}</div>
            <p className="text-white/65 text-sm font-body mt-1">{p.note}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 flex flex-wrap gap-3">
        {PROJECT_LINKS.map((l) => (
          <a
            key={l.url}
            href={l.url}
            target="_blank"
            rel="noreferrer"
            data-testid={"project-link-" + l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
            className="inline-flex items-center gap-2 text-sm text-[#00B4D8] hover:text-white border border-[#00B4D8]/25 hover:bg-[#00B4D8]/10 transition-colors px-4 py-2 rounded-full font-body"
          >
            {l.label}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Why partner ----------

const WhyPartner = () => (
  <section data-testid="why-section" className="bg-[#050708] py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="Why partner"
            title="More than reach. Industry understanding."
            intro="Most creators can make content. Most marketers understand campaigns. Few understand both the dive industry and the digital systems behind audience growth, trust and conversion."
          />
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3 md:gap-4">
          {WHY_VALUES.map((v, i) => (
            <div
              key={v}
              data-testid={"why-value-" + i}
              className="lift bg-white/3 border border-white/8 rounded-sm p-5"
            >
              <span className="stat-num text-xl text-[#00B4D8]">0{i + 1}</span>
              <p className="mt-3 text-white/85 font-body text-sm md:text-base leading-relaxed">{v}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ---------- Rates CTA banner ----------

const RateCardSection = ({ onCollab }) => (
  <section id="rates" data-testid="rates-section" className="bg-[#061826] py-24 md:py-32 border-y border-white/5 relative overflow-hidden">
    <div className="absolute -top-32 right-0 w-[40rem] h-[40rem] rounded-full bg-[#0077B6]/12 blur-3xl pointer-events-none" />
    <div className="max-w-5xl mx-auto px-5 md:px-8 relative">
      <div className="glass-dark rounded-sm p-10 md:p-14 text-center">
        <Eyebrow>Collaboration menu</Eyebrow>
        <h2 className="font-heading mt-4 text-3xl md:text-5xl font-light tracking-tight text-white">
          Full rate card available on request.
        </h2>
        <p className="mt-5 max-w-2xl mx-auto text-white/70 font-body text-base md:text-lg leading-relaxed">
          Pricing is shaped around each campaign. Sponsored content, product reviews,
          event coverage, brand consulting, content production, speaking and workshops
          all have considered rate bands in EUR. Get in touch and we will share the
          right options for your brief.
        </p>
        <div className="mt-9 flex flex-wrap gap-3 justify-center">
          <Button
            data-testid="rates-collab-btn"
            onClick={onCollab}
            size="lg"
            className="bg-[#00B4D8] text-[#050708] hover:bg-[#0077B6] hover:text-white rounded-full px-7 h-12 font-medium"
          >
            Request the rate card
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <a
            href={"mailto:" + CONTACT.email + "?subject=" + encodeURIComponent("Rate card request")}
            data-testid="rates-email-link"
            className="inline-flex items-center gap-2 px-6 h-12 rounded-full text-white/80 hover:text-white border border-white/15 hover:bg-white/5 transition-colors text-sm font-medium"
          >
            <Mail className="h-4 w-4" /> Email Robin directly
          </a>
        </div>
      </div>
    </div>
  </section>
);

// ---------- Dive Media Strategist (DMS) banner ----------

const DMSBanner = () => (
  <section id="dms" data-testid="dms-section" className="bg-[#050708] py-20 md:py-28">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <a
        href="https://app.divemediastrategist.com"
        target="_blank"
        rel="noreferrer"
        data-testid="dms-banner-link"
        className="group block relative overflow-hidden rounded-sm border border-[#00B4D8]/25 bg-gradient-to-br from-[#061826] via-[#061826] to-[#0a2c44] p-8 md:p-12 lift"
      >
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#00B4D8]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#0077B6]/15 blur-3xl pointer-events-none" />

        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00B4D8]/15 border border-[#00B4D8]/30 text-[#00B4D8] text-[10px] uppercase tracking-[0.22em] font-medium">
                <Sparkles className="h-3 w-3" /> New platform
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#FF6B5E]/15 border border-[#FF6B5E]/35 text-[#FF6B5E] text-[10px] uppercase tracking-[0.22em] font-medium">
                Beta
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#8FD6C8]/12 border border-[#8FD6C8]/30 text-[#8FD6C8] text-[10px] uppercase tracking-[0.22em] font-medium">
                Public launch, August 2026
              </span>
              <span className="text-xs text-white/45 font-body">app.divemediastrategist.com</span>
            </div>

            <h3 className="font-heading mt-5 text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white">
              Dive Media Strategist.
            </h3>
            <p className="mt-3 text-[#CAF0F8]/85 text-base md:text-lg font-heading">
              An AI-powered content and strategy platform built for the dive industry.
            </p>
            <p className="mt-4 max-w-2xl text-white/70 text-sm md:text-base font-body leading-relaxed">
              Dive Media Strategist is the evolution of a personal mission to push AI
              forward in the diving and marketing industry. It is built to help dive
              centres, gear brands, instructors and creators turn raw ideas into clear
              marketing assets, with real dive industry context baked in.
            </p>
            <p className="mt-3 max-w-2xl text-white/60 text-sm md:text-base font-body leading-relaxed">
              Content plans, captions, campaign briefs, email sequences and on-brand
              short-form concepts, generated with the language and nuance the dive
              industry actually uses. Currently in active beta, with a public launch
              planned for August 2026.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {["Content strategy", "AI workflows", "Dive industry context", "Brand voice", "Campaign planning"].map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/75 font-body"
                >
                  {t}
                </span>
              ))}
            </div>

            <div className="mt-7 inline-flex items-center gap-2 text-[#00B4D8] text-sm font-medium group-hover:gap-3 transition-all">
              Visit Dive Media Strategist
              <ExternalLink className="h-4 w-4" />
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/3] rounded-sm bg-[#050708]/60 border border-white/10 overflow-hidden shadow-2xl shadow-[#00B4D8]/10">
              {/* Browser chrome */}
              <div className="absolute top-0 inset-x-0 h-8 bg-[#050708]/90 border-b border-white/8 flex items-center px-3 gap-1.5 z-10">
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF6B5E]/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#D8C3A5]/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#8FD6C8]/70" />
                <span className="ml-3 text-[10px] text-white/55 font-body truncate">
                  app.divemediastrategist.com
                </span>
              </div>
              {/* Live screenshot via thum.io with graceful fallback */}
              <img
                src="https://image.thum.io/get/width/1200/crop/900/wait/4/noanimate/https://app.divemediastrategist.com"
                alt="Dive Media Strategist platform"
                className="absolute inset-0 w-full h-full object-cover object-top pt-8"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  const fb = e.currentTarget.nextElementSibling;
                  if (fb) fb.style.display = "flex";
                }}
              />
              {/* Fallback panel if screenshot service is unreachable */}
              <div
                className="absolute inset-0 pt-10 px-5 pb-5 hidden flex-col gap-3 bg-gradient-to-br from-[#061826] to-[#0a2c44]"
                style={{ display: "none" }}
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#00B4D8]" />
                  <span className="font-heading text-sm text-white">Dive Media Strategist</span>
                </div>
                <div className="h-3 w-3/4 rounded-sm bg-[#00B4D8]/35" />
                <div className="h-2 w-1/2 rounded-sm bg-white/15" />
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <div className="aspect-square rounded-sm bg-white/5 border border-white/10 flex items-center justify-center">
                    <Sparkles className="h-4 w-4 text-[#00B4D8]/80" />
                  </div>
                  <div className="aspect-square rounded-sm bg-white/5 border border-white/10 flex items-center justify-center">
                    <TrendingUp className="h-4 w-4 text-[#8FD6C8]/80" />
                  </div>
                  <div className="aspect-square rounded-sm bg-white/5 border border-white/10 flex items-center justify-center">
                    <Camera className="h-4 w-4 text-[#CAF0F8]/80" />
                  </div>
                </div>
                <div className="mt-2 space-y-1.5">
                  <div className="h-1.5 rounded-sm bg-white/10" />
                  <div className="h-1.5 w-5/6 rounded-sm bg-white/10" />
                </div>
                <div className="mt-2 inline-flex w-fit px-3 h-7 rounded-full bg-[#00B4D8]/25 border border-[#00B4D8]/50 items-center">
                  <span className="text-[10px] text-[#CAF0F8] font-medium">Generate</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </a>
    </div>
  </section>
);

// ---------- Proof ----------

const Proof = () => (
  <section data-testid="proof-section" className="bg-[#050708] py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <SectionHeading
        eyebrow="Proof in the numbers"
        title="Content that teaches, explains and challenges how divers think."
        intro="This is not passive consumption. It is practical dive education, gear conversation and industry storytelling that attracts comments, saves, shares and direct questions from divers."
      />

      <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        {IMAGES.proof.map((src, i) => {
          const isCarousel = i === 5 || i === 6;
          return (
            <div
              key={i}
              data-testid={"proof-img-" + i}
              className="lift relative aspect-[4/5] overflow-hidden rounded-sm bg-[#061826] group"
            >
              <img src={src} alt={"Content example " + (i + 1)} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/5" />
              <div className="absolute top-3 left-3 px-2 py-1 bg-black/55 backdrop-blur-md rounded-sm text-[10px] uppercase tracking-widest text-white/85">
                {isCarousel ? "Carousel" : "Reel"}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: Eye, v: "1.98M", l: "Top reel views" },
          { icon: Heart, v: "91.3k", l: "Reactions" },
          { icon: MessageCircle, v: "4.5k", l: "Comments" },
          { icon: Share2, v: "9.4k", l: "Shares" },
        ].map(({ icon: Icon, v, l }) => (
          <div key={l} className="lift bg-white/3 border border-white/8 rounded-sm p-5">
            <Icon className="h-4 w-4 text-[#00B4D8]" strokeWidth={1.4} />
            <div className="stat-num text-2xl text-white mt-3">{v}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/55 mt-1">{l}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Newsletter ----------

const Newsletter = () => (
  <section data-testid="newsletter-section" className="bg-[#CAF0F8] py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-6">
          <SectionHeading
            eyebrow="Newsletter"
            title="The Traveler Submerged newsletter."
            intro="The newsletter is being developed into a more focused channel for divers, creators and dive professionals. As it grows, it offers partners a direct line to warm, opt-in subscribers who already follow the brand's deeper dive education, gear and industry content."
            light
          />
        </div>
        <div className="lg:col-span-6">
          <div className="bg-[#061826] rounded-sm p-8 md:p-10 text-white">
            <div className="flex items-baseline gap-4">
              <div className="stat-num text-6xl md:text-7xl text-[#00B4D8]">393</div>
              <div className="text-white/65 font-body text-sm">current subscribers</div>
            </div>
            <Separator className="my-6 bg-white/10" />
            <div className="eyebrow text-[#CAF0F8]">Future focus</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {NEWSLETTER_FOCUS.map((n) => (
                <Pill key={n}>{n}</Pill>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---------- Contact ----------

const Contact = ({ onCollab, onDownload }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short brief.");
      return;
    }
    setSubmitting(true);
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      const axios = (await import("axios")).default;
      await axios.post(`${API}/collaboration/request`, {
        name: form.name,
        email: form.email,
        company: form.company || null,
        message: form.message,
      });
      setSubmitted(true);
      toast.success("Enquiry sent. Robin will be in touch.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#050708] py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img src={IMAGES.ocean2} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050708] via-[#050708]/85 to-[#050708]" />
      </div>
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Contact"
              title="Let's build something useful for the dive industry."
              intro="For brand partnerships, dive show collaborations, product reviews, speaking opportunities, sponsored campaigns or media enquiries, contact Robin directly."
            />

            <div className="mt-10 space-y-4 text-white/85 font-body">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-[#00B4D8]" />
                <a className="hover:text-[#00B4D8]" data-testid="contact-email-link" href={"mailto:" + CONTACT.email}>{CONTACT.email}</a>
              </div>
              <div className="flex items-center gap-3">
                <Globe2 className="h-4 w-4 text-[#00B4D8]" />
                <a className="hover:text-[#00B4D8]" target="_blank" rel="noreferrer" href={CONTACT.website}>travelersubmerged.com</a>
              </div>
              <div className="flex items-center gap-3">
                <Instagram className="h-4 w-4 text-[#00B4D8]" />
                <a className="hover:text-[#00B4D8]" target="_blank" rel="noreferrer" href={CONTACT.instagram}>@traveler_submerged</a>
              </div>
              <div className="flex items-center gap-3">
                <Music2 className="h-4 w-4 text-[#00B4D8]" />
                <a className="hover:text-[#00B4D8]" target="_blank" rel="noreferrer" href={CONTACT.tiktok}>@traveler_submerged on TikTok</a>
              </div>
              <div className="flex items-center gap-3">
                <Facebook className="h-4 w-4 text-[#00B4D8]" />
                <a className="hover:text-[#00B4D8]" target="_blank" rel="noreferrer" href={CONTACT.facebook}>Traveler Submerged</a>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-[#00B4D8]" />
                <span>{CONTACT.location}</span>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Button
                onClick={onCollab}
                data-testid="contact-collab-btn"
                size="lg"
                className="bg-[#00B4D8] text-[#050708] hover:bg-[#0077B6] hover:text-white rounded-full px-6 h-12"
              >
                Request collaboration
              </Button>
              <Button
                onClick={onDownload}
                data-testid="contact-download-btn"
                size="lg"
                variant="outline"
                className="rounded-full px-6 h-12 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              >
                <Download className="mr-2 h-4 w-4" /> Media kit
              </Button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="glass-dark rounded-sm p-7 md:p-9 space-y-5"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="c-name" className="text-[#CAF0F8]/80 text-xs uppercase tracking-widest">Name</Label>
                  <Input
                    id="c-name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={handle("name")}
                    placeholder="Your full name"
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11 focus-visible:ring-[#00B4D8]"
                  />
                </div>
                <div>
                  <Label htmlFor="c-email" className="text-[#CAF0F8]/80 text-xs uppercase tracking-widest">Email</Label>
                  <Input
                    id="c-email"
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={handle("email")}
                    placeholder="you@brand.com"
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11 focus-visible:ring-[#00B4D8]"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="c-company" className="text-[#CAF0F8]/80 text-xs uppercase tracking-widest">Company or brand</Label>
                <Input
                  id="c-company"
                  data-testid="contact-company-input"
                  value={form.company}
                  onChange={handle("company")}
                  placeholder="Optional"
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11 focus-visible:ring-[#00B4D8]"
                />
              </div>
              <div>
                <Label htmlFor="c-msg" className="text-[#CAF0F8]/80 text-xs uppercase tracking-widest">Brief</Label>
                <Textarea
                  id="c-msg"
                  data-testid="contact-message-input"
                  rows={6}
                  value={form.message}
                  onChange={handle("message")}
                  placeholder="Tell us about your brand, event or campaign, your timeline, deliverables you have in mind and any specific goals."
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#00B4D8]"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="bg-[#00B4D8] text-[#050708] hover:bg-[#0077B6] hover:text-white font-medium w-full md:w-auto rounded-full px-7 h-12 disabled:opacity-60"
              >
                {submitting ? "Sending..." : submitted ? "Sent. Send another" : "Send enquiry"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <p className="text-[11px] text-white/45 font-body">
                {submitted
                  ? "Thanks. Robin will reply within two working days."
                  : `Submissions reach Robin at ${CONTACT.email}.`}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- Footer ----------

const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#050708] border-t border-white/8 pt-16 pb-10">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid md:grid-cols-3 gap-10">
        <div>
          <img src={LOGOS.emblem} alt="Traveler Submerged" className="h-12 w-auto mb-5" />
          <p className="font-heading text-white text-base">Traveler Submerged</p>
          <p className="text-white/55 text-sm font-body mt-1">
            Dive Media, Content Strategy and Industry Partnerships.
          </p>
        </div>
        <div>
          <div className="eyebrow text-[#CAF0F8]/70">Explore</div>
          <ul className="mt-4 space-y-2 text-sm text-white/65 font-body">
            {NAV_LINKS.map((l) => (
              <li key={l.id}>
                <a href={"#" + l.id} className="hover:text-[#00B4D8]">{l.label}</a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="eyebrow text-[#CAF0F8]/70">Connect</div>
          <ul className="mt-4 space-y-2 text-sm text-white/65 font-body">
            <li><a className="hover:text-[#00B4D8]" target="_blank" rel="noreferrer" href={CONTACT.website}>travelersubmerged.com</a></li>
            <li><a className="hover:text-[#00B4D8]" target="_blank" rel="noreferrer" href={CONTACT.instagram}>Instagram</a></li>
            <li><a className="hover:text-[#00B4D8]" target="_blank" rel="noreferrer" href={CONTACT.tiktok}>TikTok</a></li>
            <li><a className="hover:text-[#00B4D8]" target="_blank" rel="noreferrer" href={CONTACT.facebook}>Facebook</a></li>
            <li><a className="hover:text-[#00B4D8]" href={"mailto:" + CONTACT.email}>{CONTACT.email}</a></li>
          </ul>
        </div>
      </div>
      <div className="mt-14 pt-6 border-t border-white/8 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs text-white/40 font-body">
        <div>© {new Date().getFullYear()} Traveler Submerged. All rights reserved.</div>
        <div className="flex items-center gap-2">
          <Anchor className="h-3.5 w-3.5" /> Built in Malta, for the dive industry.
        </div>
      </div>
    </div>
  </footer>
);

// ---------- Floating mobile CTA ----------

const FloatingCTA = ({ onCollab }) => (
  <button
    onClick={onCollab}
    data-testid="floating-cta"
    className="md:hidden fixed bottom-5 left-5 right-5 z-40 bg-[#00B4D8] text-[#050708] font-heading font-medium text-sm rounded-full h-12 flex items-center justify-center gap-2 shadow-2xl shadow-[#00B4D8]/30"
  >
    Request collaboration <ArrowRight className="h-4 w-4" />
  </button>
);

// ---------- Page ----------

export default function Landing() {
  const [collabOpen, setCollabOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);

  const openCollab = () => setCollabOpen(true);
  const openDownload = () => setDownloadOpen(true);

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#050708] text-white">
      <Nav onCollab={openCollab} onDownload={openDownload} />
      <main>
        <Hero onCollab={openCollab} onDownload={openDownload} />
        <Founder />
        <AboutBrand />
        <MediaReach />
        <Performance />
        <Platforms />
        <Audience />
        <Partnerships onCollab={openCollab} />
        <Projects />
        <WhyPartner />
        <DMSBanner />
        <RateCardSection onCollab={openCollab} />
        <Proof />
        <Newsletter />
        <Contact onCollab={openCollab} onDownload={openDownload} />
      </main>
      <Footer />
      <FloatingCTA onCollab={openCollab} />

      <MediaKitDialog open={collabOpen} onOpenChange={setCollabOpen} mode="collab" />
      <MediaKitDialog open={downloadOpen} onOpenChange={setDownloadOpen} mode="download" />
    </div>
  );
}
